import { useEffect, useState, useCallback } from "react";
import Lenis from "lenis";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { getSite, getRecaps, getGames } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Hero from "@/components/Hero";
import KineticMarquee from "@/components/KineticMarquee";
import Countdown from "@/components/Countdown";
import Scoreboard from "@/components/Scoreboard";
import Highlights from "@/components/Highlights";
import Dugout from "@/components/Dugout";
import Recaps from "@/components/Recaps";
import HowIDoIt from "@/components/HowIDoIt";
import Footer from "@/components/Footer";

const STADIUM_BG =
  "https://images.unsplash.com/photo-1556764420-e37ef4cdfa5c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwzfHxiYXNlYmFsbCUyMHN0YWRpdW0lMjBsaWdodHMlMjBuaWdodHxlbnwwfHx8fDE3ODc3OTcwODV8MA&ixlib=rb-4.1.0&q=85";

function App() {
  const [site, setSite] = useState(null);
  const [recaps, setRecaps] = useState([]);
  const [games, setGames] = useState([]);
  const [failed, setFailed] = useState(false);

  const load = useCallback(() => {
    getSite()
      .then((data) => {
        setSite(data);
        setFailed(false);
      })
      .catch(() => setFailed(true));
    getRecaps()
      .then(setRecaps)
      .catch(() => {});
    getGames()
      .then(setGames)
      .catch(() => {});
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09 });
    window.__lenis = lenis;
    let rafId;
    const loop = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(loop);
    };
    rafId = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  if (!site) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-[#050505]">
        <p
          data-testid="loading-title"
          className="font-bebas text-7xl tracking-wide text-white animate-pulse"
        >
          BIG <span className="text-neon">CAL</span>
        </p>
        <p className="mt-4 font-jbmono text-xs uppercase tracking-[0.4em] text-white/40">
          {failed ? "Could not reach the dugout - retrying..." : "Warming up..."}
        </p>
        {failed && (
          <button
            data-testid="loading-retry-button"
            onClick={load}
            className="mt-8 border border-neon px-6 py-3 font-jbmono text-xs tracking-[0.3em] text-neon transition-colors hover:bg-neon hover:text-black"
          >
            RETRY
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-[#050505] text-white antialiased overflow-x-clip">
      <div className="grain-overlay" />
      <img
        src={STADIUM_BG}
        alt=""
        className="pointer-events-none fixed inset-0 h-full w-full object-cover opacity-[0.06]"
      />
      <Toaster position="bottom-right" theme="dark" />
      <Navbar />
      <main className="relative">
        <Hero player={site.player} career={site.careerStats.career} pitching={site.pitchingStats.career} />
        <KineticMarquee />
        <Countdown countdowns={site.countdowns} milestones={site.milestones || []} gcProfile={site.gcProfile} scheduleWatch={site.scheduleWatch} onUpdated={load} />
        <Scoreboard
          careerStats={site.careerStats}
          pitchingStats={site.pitchingStats}
          gcProfile={site.gcProfile}
          upcomingSeason={site.upcomingSeason}
          games={games}
          onGamesChanged={() => getGames().then(setGames).catch(() => {})}
        />
        <Highlights clips={site.highlights} gcClips={site.gcClips || []} gcProfile={site.gcProfile} />
        <Dugout gear={site.gear} walkupSongs={site.walkupSongs} />
        <Recaps
          recaps={recaps}
          onAdded={() => getRecaps().then(setRecaps).catch(() => {})}
          openerDate={site.countdowns.tournament.date}
        />
        <HowIDoIt
          videos={site.videos}
          channelUrl={site.channelUrl}
          appleMusicUrl={site.appleMusicUrl}
        />
      </main>
      <Footer player={site.player} />
    </div>
  );
}

export default App;
